/* eslint-disable react-hooks/rules-of-hooks */
import { microsoftSignupFlow } from 'actions/auth';
import { push } from 'connected-react-router';
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks';
import { get } from 'lodash-es';
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import ScrollToTop from 'react-router-scroll-top';
import { clearLocalStorage, getLocalStorage } from 'utils/localStorage';

import { Menu, PrimaryLink } from '../../Header';
import { SpiralLoading } from '../../Loading';
import { Logo } from '../../Logo';
import { Container, Content, Header, SignUp, Title } from './styles';
import useAlreadyRegisteredError from './useAlreadyRegisteredError';

/**
 * When the user has finished authentication via microsoft
 * they are redirected back and this component rendered.
 */
const MicrosoftComplete = (): null | JSX.Element => {
  const search = new URLSearchParams(window.location.search);
  const code = search.get('code');
  const state = search.get('state');
  const notifiers = get(window, 'opener.oauth', null);

  /*
    If the user is redirected within a popup then the rest
    of the authentication flow is handled by the window
    that opened the popup.
  */
  if (notifiers) {
    const { notifySuccess, notifyFailure } = notifiers;
    if (code && state) {
      notifySuccess({ code, state });
    } else {
      notifyFailure({ code, state });
    }
    window.close();
    return null;
  }

  /*
    If the user is redirected back in the original window,
    then we assume sign up flow - was redirected via the
    marketing site is the only scenario currently.

    Hook linting disabled  because of conditional rendering
    error.  But it's safe because if it's a popup,
    the window closes anyway.  Running these hooks before
    calling the success notifiers however can prevent
    them from working.
  */
  const dispatch = useAppDispatch();
  const user = useAppSelector((stateObj) => get(stateObj, 'auth.user', {}));
  const isRegistered = useAppSelector((stateObj) =>
    get(stateObj, 'auth.user.isRegistered', {}),
  );

  /*
    If the error is detected the hook will call logout and reload
    with error state saved to localstorage.  The effect below then
    redirects back to signup.  That page will get the error state
    from local storage.
  */
  const { hasAlreadyRegisteredError } = useAlreadyRegisteredError(true);

  /**
   * If auth flow is complete and successful (got a token
   * and user isn't already registered) - redirect to confirm.
   */
  useEffect(() => {
    if (!notifiers && user && !isRegistered) {
      dispatch(push(`/signup/confirm`));
    }
  }, [notifiers, dispatch, user, isRegistered]);

  useEffect(() => {
    if (!notifiers && hasAlreadyRegisteredError) {
      dispatch(push(`/signup`));
    }
  }, [notifiers, hasAlreadyRegisteredError, dispatch]);

  /*
   * Init signup flow (after MS auth completed)
   * handles scenario where MS flow did not happen
   * in a popup.
   */
  useEffect(() => {
    const onAuthSuccess = async () => {
      if (code) {
        dispatch(microsoftSignupFlow(code, 'account'));
      }
    };

    const doSignup = async () => {
      if (!notifiers && code && state) {
        const localState = getLocalStorage('bc-fe-state', null, false);
        if (localState) {
          clearLocalStorage('bc-fe-state');
        }
        if (localState === state) {
          await onAuthSuccess();
        }
      }
    };

    doSignup();
  }, [dispatch, notifiers, code, state]);

  if (notifiers) {
    return null;
  }

  return (
    <>
      <Helmet title="Creating Account" />
      <ScrollToTop />
      <Container>
        <Header>
          <Content>
            <Logo />
            <Menu>
              Already have an account?
              <PrimaryLink to="/login">Sign in</PrimaryLink>
            </Menu>
          </Content>
        </Header>
        <SignUp>
          <Title>Welcome to BeeCastle</Title>
          <p>Creating your account. Please wait...</p>
          <SpiralLoading />
        </SignUp>
      </Container>
    </>
  );
};

export default MicrosoftComplete;
